import React, { useRef, useState } from "react";
import { Form, Input, InputNumber, Spin } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Header from "../components/signup/Header";
import PasswordChecklist from "react-password-checklist";
import circleSvg from "../../assets/CheckCircle.svg";
import { createAccount } from "../../features/auth/authSlice";
import { showToastError, showToastSuccess } from "../../utils";
import toast from "react-hot-toast";

function Step1() {
  const [form] = Form.useForm();
  const formRef = useRef(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { authLoading } = useSelector((state) => state.auth);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  function closeModal() {
    toast.dismiss();
  }

  const onFinish = async (data) => {
    if (password !== confirmPassword) {
      showToastError("Passwords do not match", closeModal);
      return;
    }

    const res = await dispatch(
      createAccount({
        firstName: data.firstName,
        lastName: data.lastName,
        phoneNumber: data.phoneNumber,
        nationalId: data.nationalId,
        email: data.email,
        password: password,
      })
    );

    if (res?.payload?.success) {
      form.resetFields();
      showToastSuccess(
        res?.payload?.message || "Account created successfully",
        closeModal
      );
      navigate("/login");
    } else {
      showToastError(
        res?.payload?.message || "Error occurred while creating account, please try again",
        closeModal
      );
    }
  };

  return (
    <>
      <div className="sticky bg-white top-0 w-full z-[1000]">
        <Header />
        <div className="lg:w-[1143px] w-auto h-[15px] bg-darkBlue"></div>

        <div className="text-center mt-[30px] text-black1 text-4xl font-bold font-dmSans">
          Create your account
        </div>

        <div className="text-center text-neutral-800 text-[22px] font-normal font-dmSans leading-relaxed">
          Make sure you give accurate details so that future employers can
          reach you.
        </div>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Form
          layout="vertical"
          ref={formRef}
          name="control-ref"
          onFinish={onFinish}
          style={{
            maxWidth: "100%",
          }}
          className="lg:w-[800px] w-[400px] lg:p-0 p-2"
          form={form}
        >
          <div className="grid grid-cols-2 gap-x-5 w-full">
            <Form.Item
              name="firstName"
              label="First Name"
              rules={[
                {
                  required: true,
                  message: "Please add first name",
                },
              ]}
            >
              <Input className="rounded-[8px] h-[44px] w-full border border-black" />
            </Form.Item>

            <Form.Item
              name="lastName"
              label="Last Name"
              rules={[
                {
                  required: true,
                  message: "Please add last name",
                },
              ]}
            >
              <Input className="rounded-[8px] h-[44px] w-full border border-black" />
            </Form.Item>
          </div>

          <Form.Item
            name="phoneNumber"
            label="Phone Number"
            rules={[
              {
                required: true,
                message: "Please add phone number",
              },
            ]}
          >
            <Input
              maxLength={10}
              className="rounded-[8px] h-[44px] w-full border border-black"
            />
          </Form.Item>

          <Form.Item
            name="nationalId"
            label="National ID Number"
            rules={[
              {
                required: true,
                message: "Please add national ID number",
              },
            ]}
          >
            <Input className="rounded-[8px] h-[44px] w-full border border-black" />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              {
                required: true,
                message: "Please add email",
              },
              {
                type: "email",
                message: "Please enter a valid email",
              },
            ]}
          >
            <Input
              type="email"
              className="rounded-[8px] h-[44px] w-full border border-black"
            />
          </Form.Item>

          <div className="grid grid-cols-2 gap-x-5 w-full">
            <Form.Item
              name="password"
              label="Password"
              rules={[
                {
                  required: true,
                  message: "Please add password",
                },
              ]}
            >
              <Input.Password
                className="rounded-[8px] h-[44px] w-full border border-black"
                onChange={(e) => setPassword(e.target.value)}
              />
            </Form.Item>

            <Form.Item
              name="confirmPassword"
              label="Confirm Password"
              rules={[
                {
                  required: true,
                  message: "Please confirm your password",
                },
              ]}
            >
              <Input.Password
                className="rounded-[8px] h-[44px] w-full border border-black"
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </Form.Item>
          </div>

          <PasswordChecklist
            className="text-darkBlue"
            rules={["minLength", "specialChar", "number", "capital", "match"]}
            minLength={8}
            value={password}
            valueAgain={confirmPassword}
            messages={{
              minLength: "A minimum of 8 characters",
              specialChar: "At least one symbol (~!@$&)",
              number: "At least one number (0-9)",
              capital: "At least one upper case letter (A-Z)",
              match: "Confirm password to match with the password",
            }}
            iconComponents={{
              ValidIcon: (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  className="w-6 h-6 text-darkBlue"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              ),
              InvalidIcon: <img src={circleSvg} />,
            }}
            validColor="#147CBC"
            iconSize={2}
          />

          <div className="text-center mt-[20px]">
            <button
              disabled={authLoading}
              className="text-white text-[18px] font-bold font-dmSans
                    w-[299px] h-[53px] px-16 py-[19px] bg-darkBlue rounded-[38px]
                    justify-center items-center gap-2.5 inline-flex"
            >
              {authLoading ? <Spin className="text-white" /> : "Create account"}
            </button>
          </div>

          <div className="text-center mt-[20px] mb-[40px]">
            <div
              className="cursor-pointer text-darkBlue text-xl font-normal font-dmSans leading-normal
				w-[299px] h-[53px] pl-[18px] pr-[17.20px] pt-2 pb-[7.50px] bg-white rounded-[35px]
				shadow border-2 border-darkBlue justify-center items-center inline-flex"
              onClick={() => navigate("/login")}
            >
              Back to login
            </div>
          </div>
        </Form>
      </div>
    </>
  );
}

export default Step1;
